import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import logo from "../assets/zarvika_logo.png";
import {
  HomeIcon,
  UserIcon,
  CubeIcon,
  ChartBarIcon,
  DocumentIcon,
  UsersIcon,
  CogIcon,
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

function Sidebar({ user, isOpen, setIsOpen }) {
  const adminMenu = [
    { name: "Dashboard", icon: HomeIcon, path: "/dashboard" },
    { name: "Jewellers", icon: UserIcon, path: "/jewellers" },
    { name: "Subscriptions", icon: CubeIcon, path: "/subscriptions" },
    { name: "Add-ons", icon: DocumentIcon, path: "/addons" },
    { name: "Analytics", icon: ChartBarIcon, path: "/analytics" },
    { name: "Activity Logs", icon: DocumentIcon, path: "/activity" },
    { name: "Settings", icon: CogIcon, path: "/settings" },
  ];

  const jewellerMenu = [
    { name: "Dashboard", icon: HomeIcon, path: "/jewellers/dashboard" },
    { name: "Staff", icon: UsersIcon, path: "/jewellers/Staff" },
    { name: "Customers", icon: UserIcon, path: "/jewellers/customers" },
    { name: "Kitty", icon: CubeIcon, path: "/jewellers/kittyList" },
    { name: "Customers Kitty", icon: CubeIcon, path: "/jewellers/CustomerKitties" },
    // { name: "Settings", icon: CogIcon, path: "/jewellers/settings" },
    { name: "Customers payment", icon: CubeIcon, path: "/jewellers/CustomerPayment" },
    { name: "Settings", icon: CogIcon, path: "/jewellers/businessdetails" },
  ];

  const staffMenu = [
    { name: "Dashboard", icon: HomeIcon, path: "/jewellers/dashboard" },
    { name: "Customers", icon: UserIcon, path: "/jewellers/customers" },
    { name: "Kitty", icon: CubeIcon, path: "/jewellers/kittyList" },
  ];

  const customersMenu = [
    { name: "Dashboard", icon: HomeIcon, path: "/customers/dashboard" },
    { name: "Profile", icon: UserIcon, path: "/customers/profile" },
  ];

  const menuItems = user
    ? user.role_id === 1
      ? adminMenu
      : user.role_id === 2
        ? jewellerMenu
        : user.role_id === 3
          ? customersMenu
          : user.role_id === 4
            ? staffMenu
            : []
    : [];

  return (
    <>
      <aside
        className={`fixed lg:static top-0 left-0 w-64 bg-gradient-to-b from-purple-500 via-fuchsia-700 to-purple-600 text-white flex flex-col p-6 shadow-2xl overflow-hidden transform transition-transform duration-300 z-50
          ${isOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
      >
        {/* Decorative Glow Orbs */}
        <div className="absolute top-0 left-0 w-48 h-48 bg-purple-400/30 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-56 h-56 bg-fuchsia-500/20 rounded-full blur-3xl translate-x-1/3 translate-y-1/3"></div>

        {/* Logo */}
        <div className="mb-10 flex justify-center relative z-10">
          <Link
            to={user?.role_id === 2 ? "/jewellers/dashboard" : "/dashboard"}
            onClick={() => setIsOpen(false)}
          >
            <div className="p-3 transform transition-all hover:scale-[1.15] hover:shadow-purple-300/20 backdrop-blur-md rounded-2xl duration-300">
              <img src={logo} alt="Zarvika Logo" className="h-14 w-auto" />
            </div>
          </Link>
        </div>

        {/* Nav */}
        <nav className="flex flex-col space-y-2 relative z-10">
          {menuItems.map((item) => (
            <Link
              key={item.name}
              to={item.path}
              onClick={() => setIsOpen(false)}
              className="flex items-center px-4 py-3 rounded-xl transition-all duration-300 group 
                 hover:bg-white/15 hover:backdrop-blur-sm hover:scale-[1.05] transform hover:shadow-lg"
            >
              <item.icon
                className="h-5 w-5 mr-3 text-fuchsia-200 group-hover:text-white transition-colors duration-300"
              />
              <span className="text-sm font-medium tracking-wide group-hover:text-white">
                {item.name}
              </span>
            </Link>
          ))}
        </nav>

        {/* Footer */}
        <div className="mt-auto text-center text-xs text-purple-200 pt-6 border-t border-white/10 z-10">
          © {new Date().getFullYear()}{" "}
          <span className="font-semibold text-white">Zarvika</span>
        </div>
      </aside>

      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
        ></div>
      )}
    </>
  );
}

export default function Layout({ children, user: propUser, setUser }) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const user = propUser || JSON.parse(localStorage.getItem("user"));

  if (!user) {
    navigate("/login");
    return null;
  }

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    setUser(null);
    navigate("/login");
  };

  return (
    <div className="flex flex-col lg:flex-row min-h-screen bg-gray-100 overflow-hidden">
      {/* Sidebar */}
      <Sidebar user={user} isOpen={isOpen} setIsOpen={setIsOpen} />

      {/* Content Section */}
      <div className="flex-1 flex flex-col w-full">
        {/* Header */}
        <header className="flex justify-between items-center bg-gradient-to-r from-purple-500 via-fuchsia-400 to-purple-500 shadow-lg p-4 text-white sticky top-0 z-40 rounded-none lg:rounded-b-2xl">
          <div className="flex items-center space-x-3">
            {/* Mobile Toggle */}
            <button
              className="lg:hidden focus:outline-none"
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? (
                <XMarkIcon className="h-6 w-6 text-white" />
              ) : (
                <Bars3Icon className="h-6 w-6 text-white" />
              )}
            </button>
            <h1 className="text-lg sm:text-xl font-semibold drop-shadow-md leading-snug">
              Welcome,&nbsp;
              {user?.name
                ? user.name.charAt(0).toUpperCase() + user.name.slice(1)
                : "User"}
              !
            </h1>
          </div>

          <button
            onClick={handleLogout}
            className="bg-white/20 backdrop-blur-md text-white px-4 py-2 rounded-lg hover:bg-white/30 transition-all duration-300 font-medium shadow-inner text-sm sm:text-base"
          >
            Logout
          </button>
        </header>

        {/* Main */}
        <main className="flex-1 p-4 sm:p-6 mt-2 overflow-y-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
